import React, { memo, useContext, useState, useCallback } from "react";
import { BoltStampContainer } from "./styled";
import { CartOverlaysContext } from "@swiggy-private/checkout-core";
import { LottieIds } from "@swiggy-private/cart-types";
import {
    BoltOtgStampLottie,
    BoltRangeSlaStampLottie,
    BoltSla10StampLottie,
    BoltSla15StampLottie,
} from "./BoltStampLotties";
import { CART_HEADER_DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";

export interface IBoltLottieProps {
    isBlack: boolean;
    isOtg: boolean;
    sla: number;
    showBoltLottie: boolean;
    showFinalState?: boolean;
    isRangeSla: boolean;
    setOtgLottieShown: React.Dispatch<React.SetStateAction<boolean>>;
}

const BoltStampLottie = ({
    isBlack,
    isOtg,
    showBoltLottie,
    sla,
    showFinalState,
    isRangeSla,
    setOtgLottieShown,
}: IBoltLottieProps) => {
    const [isError, setIsError] = useState(false);
    const { onCloseOverlay, enabledOverlayId } = useContext(CartOverlaysContext);

    const handleError = useCallback(() => {
        if (enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
            onCloseOverlay();
            setIsError(true);
        }
        setIsError(true);
    }, [enabledOverlayId, onCloseOverlay]);

    const onPlayComplete = useCallback(() => {
        if (!showFinalState && enabledOverlayId === LottieIds.CART_HEADER_LOTTIE) {
            onCloseOverlay();
        }
        if (isBlack && isOtg) {
            setOtgLottieShown(true);
        }
    }, [showFinalState, enabledOverlayId, isBlack, isOtg, onCloseOverlay, setOtgLottieShown]);

    if (!showBoltLottie) {
        return null;
    }

    const renderLottie = () => {
        if (isBlack && isOtg) {
            return (
                <BoltOtgStampLottie
                    handleError={handleError}
                    onPlayComplete={onPlayComplete}
                    showBoltLottie={showBoltLottie}
                    showFinalState={showFinalState ?? false}
                />
            );
        } else if (isRangeSla) {
            return (
                <BoltRangeSlaStampLottie
                    handleError={handleError}
                    onPlayComplete={onPlayComplete}
                    showBoltLottie={showBoltLottie}
                    showFinalState={showFinalState ?? false}
                />
            );
        } else if (sla <= 10) {
            return (
                <BoltSla10StampLottie
                    handleError={handleError}
                    onPlayComplete={onPlayComplete}
                    showBoltLottie={showBoltLottie}
                    showFinalState={showFinalState ?? false}
                />
            );
        } else if (sla <= 15) {
            return (
                <BoltSla15StampLottie
                    handleError={handleError}
                    onPlayComplete={onPlayComplete}
                    showBoltLottie={showBoltLottie}
                    showFinalState={showFinalState ?? false}
                />
            );
        }
        return null;
    };

    if (isError) {
        return null;
    }

    return (
        <BoltStampContainer
            data-testId={
                isBlack && isOtg
                    ? CART_HEADER_DATA_TEST_IDS.BOLT_OTG_STAMP_LOTTIE
                    : CART_HEADER_DATA_TEST_IDS.BOLT_STAMP_LOTTIE
            }
        >
            {renderLottie()}
        </BoltStampContainer>
    );
};

export default memo(BoltStampLottie);
